import { useEffect, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import { SEARCH_HINTS } from "@hub/shared";
import { IconButton, Input, Pill } from "@/ui";
import { useEngineQuery } from "@/engine/hooks";
import s from "../shared.module.css";

/**
 * Grammar-aware search box. Hint chips insert a term; the parsed query renders as readable chips underneath so the
 * user sees how Hub understood it. `debounce` keeps list queries from firing on every keystroke.
 */
export function SearchBox({ value, onChange, placeholder = "Search people, or try company:acme", autoFocus, showHints = true, showParsed = true, debounce = 180, onSubmit }: { value: string; onChange: (q: string) => void; placeholder?: string; autoFocus?: boolean; showHints?: boolean; /** Off where a page shows the chip row itself. */ showParsed?: boolean; debounce?: number; onSubmit?: (q: string) => void }) {
  const [draft, setDraft] = useState(value);
  const [focused, setFocused] = useState(false);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => setDraft(value), [value]);
  useEffect(() => {
    if (draft === value) return;
    const t = setTimeout(() => onChange(draft), debounce);
    return () => clearTimeout(t);
  }, [draft, value, onChange, debounce]);
  const parsed = useEngineQuery("parseSearch", { query: value }, { enabled: showParsed && value.trim().length > 0, staleTime: 60_000 });

  const insert = (term: string) => {
    const next = draft.trim() ? `${draft.trim()} ${term}` : term;
    setDraft(next);
    onChange(next);
    ref.current?.focus();
  };

  return (
    <div className="u-col" style={{ gap: 6, flex: 1, minWidth: 0 }}>
      <div className={s.searchWrap}>
        <Search size={15} className={s.searchIcon} />
        <Input
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 150)}
          ref={ref}
          data-search-box
          className={s.searchInput}
          value={draft}
          autoFocus={autoFocus}
          placeholder={placeholder}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              onChange(draft);
              onSubmit?.(draft);
            }
            if (e.key === "Escape") {
              if (draft) {
                setDraft("");
                onChange("");
              } else (e.target as HTMLInputElement).blur();
              e.stopPropagation();
            }
          }}
          aria-label="Search"
        />
        {draft && (
          <span style={{ position: "absolute", right: 4, top: 2 }}>
            <IconButton label="Clear search" onClick={() => { setDraft(""); onChange(""); }}>
              <X size={14} />
            </IconButton>
          </span>
        )}
      </div>
      {showHints && focused && !value.trim() && (
        <div className={s.chips} data-testid="search-hints">
          {SEARCH_HINTS.slice(0, 8).map((h) => (
            <button key={h.term} type="button" className={s.hint} onClick={() => insert(h.example)} title={h.description}>
              {h.term}
              <span className={s.hintDesc}>{h.description}</span>
            </button>
          ))}
        </div>
      )}
      {showParsed && value.trim() && parsed.data && (parsed.data.chips.length > 0 || parsed.data.warnings.length > 0) && (
        <div className={s.chips} data-testid="search-parsed">
          {parsed.data.chips.map((c, i) => (
            <Pill key={i} tone="accent">{c}</Pill>
          ))}
          {parsed.data.freeText && <Pill>Text: {parsed.data.freeText}</Pill>}
          {parsed.data.warnings.map((w, i) => (
            <Pill key={`w${i}`} tone="warning">{w}</Pill>
          ))}
        </div>
      )}
    </div>
  );
}
